import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { AppRoute } from '@app/shared/app.route.enum';
import { environment } from '@env/environment';
import { __, MbLoadingBarService, MbResponse } from '@montblancsimpl/core';
import { Observable, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

import { AuthenticationService, Credentials } from './authentication.service';

/**
 * The authentication guard ensures that the accessing user is an umc boarded user,
 * has at least one permissions for this application, has not an expired login and
 * additionally can check if the user is authorized by setting the data attribute
 * of a route to: data: { permissions: ['permission1'] }
 *
 * @export
 * @class AuthenticationGuard
 * @implements {CanActivate}
 */
@Injectable()
export class AuthenticationGuard {
  constructor(
    private router: Router,
    private authenticationService: AuthenticationService,
    private loadingBarService: MbLoadingBarService,
  ) {}

  canActivate(route: any): Observable<boolean> {
    if (this.authenticationService.isAuthenticated()) {
      return this.isAuthorized(route);
    } else {
      return this.login(route);
    }
  }

  private isAuthorized(route: ActivatedRouteSnapshot): Observable<boolean> {
    // The user has at least at one point requested access from the umc to this application
    if (this.authenticationService.hasStoredPermissions()) {
      // user has at least one permission for this application
      if (this.authenticationService.isAuthenticationExpired()) {
        // User was authorized before, so we check if he still is
        return this.login(route);
      } else {
        // The user has a valid login, so we check if he has the required permissions for this route
        if (environment.production === false) {
          const isQATester = this.authenticationService.hasAnyPermission([
            environment.umcQATesterRole,
          ]);
          if (!isQATester) {
            console.log('navigateToForbidden', '!isQATester');
            this.navigateToForbidden();
          }
          if (environment.testPermissions === false) {
            return of(isQATester);
          }
        }

        let hasRequiredPermissions = true;
        if (
          !__.IsNullOrUndefined(route.data['permissions']) &&
          route.data['permissions'].length > 0
        ) {
          hasRequiredPermissions = this.authenticationService.hasAnyPermission(
            route.data['permissions'],
          );
        } else if (!__.IsNullOrUndefined(route.data['roles']) && route.data['roles'].length > 0) {
          hasRequiredPermissions =
            (<string[]>route.data['roles']).findIndex((q) => {
              return Object.values(this.authenticationService.getRolesOfCurrentLoggedInUser());
            }) > -1;
        }

        if (!hasRequiredPermissions) {
          console.log('navigateToForbidden', '!hasRequiredPermission');
          this.navigateToForbidden();
        }

        return of(hasRequiredPermissions);
      }
    } else {
      if (this.authenticationService.isAuthenticationExpired()) {
        return this.login(route);
      }
      // The user has zero permissions
      console.log('navigateToForbidden', 'zero permissions');
      this.navigateToForbidden();
      return of(false);
    }
  }

  private login(route: ActivatedRouteSnapshot): Observable<boolean> {
    return this.authenticationService
      .login()
      .pipe(mergeMap((credentials: Credentials) => this.isAuthorized(route)));
  }

  private navigateToForbidden() {
    this.router.navigate([`/${AppRoute.Forbidden}`]).then((value: boolean) => {
      this.loadingBarService.complete();
    });
  }
}
